import { capitalize } from 'lodash';
import { IExcelReportData, IRowCells, ITableData } from 'types/excelExport';
import { EXCEL_COLORS } from 'views/ProFormaTable/helpers/excelExport/constants';
import { TimelineStatus } from '../constants';
import { getUngroupedTimelines, groupTimelinesBySection, htmlToPlainText } from '../helpers';
import { TimelineItem } from '../types';
import {
    TASKS_EXPORT_COLUMNS, TASKS_EXPORT_DATE_NUM_FMT, TASKS_EXPORT_ROW_HEIGHT,
} from './constants';
import { toExcelDate, truncateForCell } from './helpers';
import { IBuildTasksExportDataParams } from './types';

const COLUMN_COUNT = TASKS_EXPORT_COLUMNS.length;

const buildHeaderRow = (): ITableData => ({
    rowCells: TASKS_EXPORT_COLUMNS.map(column => ({
        value: column.header,
        width: column.width,
        cellFont: { bold: true, color: { argb: EXCEL_COLORS.WHITE } },
        alignment: { horizontal: 'left', vertical: 'top' },
    })),
    rowFill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: EXCEL_COLORS.BLUE_HEADER },
    },
});

const buildSectionRow = (name: string): ITableData => ({
    rowCells: [
        { value: name, cellFont: { bold: true }, alignment: { horizontal: 'left', vertical: 'top' } },
        ...Array.from({ length: COLUMN_COUNT - 1 }, (): IRowCells => ({ value: '' })),
    ],
    rowFill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: EXCEL_COLORS.DARK_GRAY },
    },
});

const STATUS_FILL_COLORS: Partial<Record<TimelineStatus, string>> = {
    [TimelineStatus.OVERDUE]: EXCEL_COLORS.LIGHT_RED,
    [TimelineStatus.COMPLETE]: EXCEL_COLORS.LIGHT_GREEN,
};

const buildStatusHighlight = (status: TimelineItem['status']): Pick<IRowCells, 'cellFill'> => {
    const argb = STATUS_FILL_COLORS[status];
    return argb ? { cellFill: { type: 'pattern', pattern: 'solid', fgColor: { argb } } } : {};
};

const buildTaskRow = (
    item: TimelineItem,
    { latestComments, resolveAssigneeName }: Pick<IBuildTasksExportDataParams, 'latestComments' | 'resolveAssigneeName'>,
): ITableData => {
    const target = toExcelDate(item.target);
    const completed = toExcelDate(item.completed);
    const assignees = (item.assignees ?? [])
        .map(assignee => resolveAssigneeName(assignee.userCorrelationId))
        .filter(Boolean)
        .join(', ');
    const latestCommentHtml = latestComments?.[item.id]?.bodyHtml;
    const latestComment = latestCommentHtml ? truncateForCell(htmlToPlainText(latestCommentHtml)) : '';
    const topLeft = { horizontal: 'left', vertical: 'top' };

    return {
        rowHeight: TASKS_EXPORT_ROW_HEIGHT,
        rowCells: [
            { value: item.name, alignment: topLeft },
            { value: target ?? '', numFmt: target ? TASKS_EXPORT_DATE_NUM_FMT : undefined, alignment: topLeft },
            { value: completed ?? '', numFmt: completed ? TASKS_EXPORT_DATE_NUM_FMT : undefined, alignment: topLeft },
            { value: assignees, alignment: topLeft },
            { value: capitalize(item.status), alignment: topLeft, ...buildStatusHighlight(item.status) },
            { value: latestComment, alignment: { ...topLeft, wrapText: true } },
            { value: item.attachmentCount ?? 0, alignment: topLeft },
        ],
    };
};

/**
 * Builds the Deal Tasks worksheet: a blue header row, then the ungrouped activities,
 * then each section as a grey row followed by its activities — the same order as the UI.
 * A deal with no activities yields the header row on its own.
 */
export const buildTasksExportData = ({
    timelines,
    sections,
    latestComments,
    resolveAssigneeName,
}: IBuildTasksExportDataParams): IExcelReportData => {
    const timelinesBySection = groupTimelinesBySection(timelines);
    const rowOptions = { latestComments, resolveAssigneeName };

    const tableData: ITableData[] = getUngroupedTimelines(timelines).map(item => buildTaskRow(item, rowOptions));

    sections.forEach(section => {
        tableData.push(buildSectionRow(section.name));
        (timelinesBySection.get(section.id) ?? []).forEach(item => tableData.push(buildTaskRow(item, rowOptions)));
    });

    return {
        topHeaderRows: [],
        reportTables: [{ tableHeaderRows: [buildHeaderRow()], tableData }],
    };
};
